import { AnnotationDataBoundingBox } from '../types/models/annotation'
import { Tag } from '../types/models/tag'

// Types to support future phases while keeping Phase 0 usage minimal
export type ExportStatus = 'idle' | 'creating' | 'preparing' | 'uploading' | 'success' | 'error'

export type ExportPhase =
  | 'idle'
  | 'creating_project'
  | 'preparing_images_and_labels'
  | 'uploading_content'
  | 'done'
  | 'error'

export interface ExportProgressByTag {
  done: number
  total: number
}

export interface ExportProgress {
  current: number
  total: number
  byTag: Record<string, ExportProgressByTag>
  currentPhase: ExportPhase
}

export interface ExportOptions {
  imageSize: number
  uploadAsUnannotated: boolean
  datasetSplit: 'training' | 'validation'
}

export interface ExportItem {
  contentId: string
  boxes: AnnotationDataBoundingBox[]
}

export interface ExportResult {
  totalUploaded: number
  byTag: Record<string, number>
}

export const uuidV4 = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID()
  }
  // Fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Resolve a human-readable tag name for a bounding box using the available tags list.
 * Falls back to class_name, then name, then "unknown" if not resolvable.
 */
export const resolveTagName = (box: AnnotationDataBoundingBox, tags: Tag[] | undefined): string => {
  if (tags && tags.length > 0) {
    const match = tags.find((t) => t.id === (box as any).class_name || t.id === (box as any).name)
    if (match?.name) return match.name
  }
  const raw = (box as any).class_name || (box as any).name
  return raw || 'unknown'
}

/**
 * Group bounding boxes by resolved tag name.
 */
export const groupBoxesByTag = (
  boxes: AnnotationDataBoundingBox[],
  tags: Tag[] | undefined
): Record<string, AnnotationDataBoundingBox[]> => {
  return boxes.reduce<Record<string, AnnotationDataBoundingBox[]>>((acc, box) => {
    const tagName = resolveTagName(box, tags)
    if (!acc[tagName]) acc[tagName] = []
    acc[tagName].push(box)
    return acc
  }, {})
}

/**
 * Generate a stable set of PNG filenames for a group of boxes.
 */
export const generateFilenames = (count: number): string[] => {
  const filenames: string[] = []
  for (let i = 0; i < count; i++) {
    filenames.push(uuidV4() + '.png')
  }
  return filenames
}

/**
 * Build a labels.json File and corresponding filenames for a tag group.
 * When includeEntries=false, returns an empty labels file with [] as content.
 */
export const buildLabelsFile = (
  groupBoxes: AnnotationDataBoundingBox[],
  tagName: string,
  includeEntries: boolean
): { file: File; filenames: string[] } => {
  const filenames = generateFilenames(groupBoxes.length)
  const entries = includeEntries
    ? filenames.map((external_id) => ({
        tags: [tagName],
        external_id,
        metadata: { bounding_boxes: [] as AnnotationDataBoundingBox[] },
      }))
    : []

  const json = JSON.stringify(entries)
  const file = new File([json], 'labels.json', { type: 'application/json' })
  return { file, filenames }
}
